(ns ballot-scout-v2-frontend.widget.events
  (:require
   [ajax.edn :refer [edn-request-format edn-response-format]]
   [ballot-scout-v2-frontend.api-urls :as urls]
   [ballot-scout-v2-frontend.util :as util]
   [ballot-scout-v2-frontend.widget.i18n :as i18n]
   [clojure.set :as set]
   [day8.re-frame.async-flow-fx]
   [day8.re-frame.http-fx]
   [goog.events :as gev]
   [goog.events.KeyCodes :as keycodes]
   [re-frame.core :as rf])
  (:import [goog.events KeyHandler]))

(rf/reg-event-db
 ::set-widget-type
 (fn [db [_ partner-or-jurisdiction]]
   (assoc-in db [:widget :type] partner-or-jurisdiction)))

(rf/reg-event-db
 ::set-form-phase
 (fn [db [_ new-form-phase]]
   (assoc-in db [:widget :phase] new-form-phase)))

(rf/reg-event-db
 ::set-widget-ballot-id
 (fn [db [_ ballot-id]]
   (assoc-in db [:widget :ballot-id] ballot-id)))

(rf/reg-event-fx
 ::fetch-ballots-by-voter-id
 (fn [_ [_ params]]
   (let [query-params (->> [:voter-id
                            :voter.fullname
                            :voter.address-1
                            :voter.address-2
                            :voter.city
                            :voter.state
                            :voter.zip
                            :jurisdiction-id
                            :partner-id]
                           (select-keys params)
                           (util/remove-nils))]
     {:http-xhrio {:method :get
                   :params query-params
                   :uri (urls/ballot-search)
                   :format (edn-request-format)
                   :response-format (edn-response-format)
                   :on-success [::fetch-ballots-success]
                   :on-failure [::fetch-ballots-failure]}})))

(rf/reg-event-fx
 ::fetch-voters-for-widget
 (fn [_ [_ search-params]]
   (let [query-params (-> (merge search-params)
                          (util/trim-values)
                          (set/rename-keys {:street :address-1
                                            :street-2 :address-2
                                            :city :city
                                            :state :state
                                            :zip :zip}))]
     {:http-xhrio {:method :get
                   :params query-params
                   :uri (urls/voter-search)
                   :format (edn-request-format)
                   :response-format (edn-response-format)
                   :on-success [::fetch-voters-success]
                   :on-failure [::fetch-voters-failure]}})))

(rf/reg-event-db
 ::clear-ballot-response
 (fn [db _]
   (assoc-in db [:widget :ballot-response] nil)))

(rf/reg-event-db
 ::clear-voter-response
 (fn [db _]
   (assoc-in db [:widget :voter-response] nil)))

(rf/reg-event-db
 ::set-search-params
 (fn [db [_ search-params]]
   (assoc-in db [:widget :search-params] search-params)))

(rf/reg-event-fx
 ::fetch-ballots-success
 (fn [{:keys [db]} [_ ballot-response]]
   (let [in-last-6-mo (filter #(util/within-six-months? (:election/date (:ballot/election %))) ballot-response)
         widget-phase-fx (cond (> (count in-last-6-mo) 1)  ; set phase to multiple ballots -- presumably multiple elections
                               [[:dispatch [::set-form-phase :multiple-elections]]]

                               (< (count in-last-6-mo) 1)
                               [[:dispatch [::set-form-phase :not-found]]]

                               (= (count in-last-6-mo) 1)
                               [[:dispatch [::set-form-phase :show-result]]
                                [:dispatch [::set-widget-ballot-id (:ballot/id (first in-last-6-mo))]]])]
     {:db (assoc-in db [:widget :ballot-response] in-last-6-mo)
      :fx widget-phase-fx})))

(rf/reg-event-db
 ::fetch-ballots-failure
 (fn [db _]
   (assoc-in db [:widget :ballot-response] [])
   (assoc-in db [:widget :phase] :no-results)))

(rf/reg-event-fx
 ::fetch-voters-success
 (fn [{:keys [db]} [_ response]]
   (let [to-dispatch
         (cond (= (count response) 1)
               (let [voter (first response)
                     voter-params {:voter-id (:voter/id voter)
                                   :voter.fullname (:voter/fullname voter)
                                   :voter.address-1 (:voter/address-1 voter)
                                   :voter.address-2 (:voter/address-2 voter)
                                   :voter.city (:voter/city voter)
                                   :voter.state (:voter/state voter)
                                   :voter.zip (:voter/zip voter)}]  ; grab the voter-id, then do a ballot search for this voter
                 [::fetch-ballots-by-voter-id (assoc voter-params :jurisdiction-id (:voter/jurisdiction-id (first response)))])

               (> (count response) 1)
               [::set-form-phase :multiple-voters]

               (< (count response) 1)
               [::set-form-phase :not-found])]
     {:db (assoc-in db [:widget :voter-response] response)
      :fx [[:dispatch to-dispatch]]})))

(rf/reg-event-db
 ::fetch-voters-failure
 (fn [db _]
   (assoc-in db [:widget :voter-response] [])
   (assoc-in db [:widget :phase] :no-results)))

(rf/reg-event-db
 ::set-lang-code
 (fn [db [_ lang-code]]
   (i18n/set-datetime-patterns lang-code)
   (assoc-in db [:widget :langcode] lang-code)))

(rf/reg-event-db
 ::widget-fetch-partner-success
 (fn [db [_ _ response]]
   (assoc-in db [:widget :partner] response)))

(rf/reg-event-db
 ::widget-fetch-partner-failure
 (fn [db [_ _ response]]
   (assoc-in db [:widget :fetch-partner-error] response)
   (assoc-in db [:widget :partner] nil)))

(rf/reg-event-fx
 ::widget-fetch-partner
 (fn [_ [_ partner-slug]]
   {:http-xhrio {:method :get
                 :uri (urls/widget-partner-data partner-slug)
                 :format (edn-request-format)
                 :response-format (edn-response-format)
                 :on-success [::widget-fetch-partner-success partner-slug]
                 :on-failure [::widget-fetch-partner-failure partner-slug]}}))

(rf/reg-event-fx
 ::load-jurisdiction-from-route-for-widget
 (fn [_ [_ route]]
   (let [jurisdiction-slug (get-in route [:path-params :jurisdiction-slug])]
     {:dispatch-n [[::set-widget-type :jurisdiction]
                   [::widget-fetch-jurisdiction jurisdiction-slug]
                   [::set-form-phase :init]
                   [::clear-ballot-response]]})))

(rf/reg-event-fx
 ::load-partner-from-route-for-widget
 (fn [_ [_ route]]
   (let [partner-slug (get-in route [:path-params :partner-slug])]
     {:dispatch-n [[::set-widget-type :partner]
                   [::widget-fetch-partner partner-slug]
                   [::set-form-phase :init]
                   [::clear-ballot-response]]})))

(rf/reg-event-fx
 ::widget-fetch-jurisdiction
 (fn [_ [_ jurisdiction-slug]]
   {:http-xhrio {:method :get
                 :uri (urls/widget-jurisdiction-data jurisdiction-slug)
                 :format (edn-request-format)
                 :response-format (edn-response-format)
                 :on-success [::widget-fetch-jurisdiction-success jurisdiction-slug]
                 :on-failure [::widget-fetch-jurisdiction-failure jurisdiction-slug]}}))

(rf/reg-event-fx
 ::widget-fetch-jurisdiction-success
 (fn [{:keys [db]} [_ jurisdiction-slug response]]
   {:db (-> db
            (assoc-in [:widget :jurisdiction] response)
            (assoc-in [:jurisdictions jurisdiction-slug] response))
    :dispatch-n [[::widget-fetch-partner (:jurisdiction/partner-id response)]]}))

(rf/reg-event-db
 ::widget-fetch-jurisdiction-failure
 (fn [db [_ _ response]]
   (assoc-in db [:widget :fetch-jurisdiction-error] response)))

(rf/reg-event-db
 ::set-widget-voter
 (fn [db [_ voter-id]]
   (assoc-in db [:widget :voter] voter-id)))

;; SMS subscriptions

(rf/reg-event-fx
 ::voter-sms-subscribe
 (fn [{:keys [db]} [_ jurisdiction-id voter-id params]]
   {:db (assoc-in db [:widget :subscriptions :sms :subscribe] :standby)
    :http-xhrio {:method :post
                 :uri (urls/voter-sms-subscribe jurisdiction-id voter-id)
                 :params params
                 :format (edn-request-format)
                 :response-format (edn-response-format)
                 :on-success [::voter-sms-subscribe-success]
                 :on-failure [::voter-sms-subscribe-failure]}}))

(rf/reg-event-db
 ::voter-sms-subscribe-success
 (fn [db [_ response]]
   (assoc-in db
             [:widget :subscriptions :sms]
             {:subscribe :sent
              :subscribe-response response})))

(rf/reg-event-db
 ::voter-sms-subscribe-failure
 (fn [db [_ response]]
   (assoc-in db
             [:widget :subscriptions :sms]
             {:subscribe-error response})))

(rf/reg-event-db
 ::voter-sms-subscribe-reset
 (fn [db [_ _]]
   (assoc-in db [:widget :subscriptions :sms] nil)))

(rf/reg-event-fx
 ::voter-sms-unsubscribe
 (fn [_ [_ jurisdiction-id voter-id params]]
   {:http-xhrio {:method :put
                 :uri (urls/voter-sms-unsubscribe jurisdiction-id voter-id)
                 :params params
                 :format (edn-request-format)
                 :response-format (edn-response-format)
                 :on-success [::voter-sms-unsubscribe-success]
                 :on-failure [::voter-sms-unsubscribe-failure]}}))

(rf/reg-event-db
 ::voter-sms-unsubscribe-success
 (fn [db [_ response]]
   (assoc-in db
             [:widget :subscriptions :sms]
             {:unsubscribe :sent
              :unsubscribe-response response})))

(rf/reg-event-db
 ::voter-sms-unsubscribe-failure
 (fn [db [_ response]]
   (assoc-in db
             [:widget :subscriptions :sms]
             {:unsubscribe-error response})))

(rf/reg-event-fx
 ::voter-sms-check-and-subscribe
 (fn [_ [_ jurisdiction-id voter-id params]]
   {:http-xhrio {:method :get
                 :uri (urls/check-stopcode-received)
                 :params {:voter-id voter-id
                          :jurisdiction-id jurisdiction-id
                          :from-number (:phone params)}
                 :format (edn-request-format)
                 :response-format (edn-response-format)
                 :on-success [::voter-sms-check-and-subscribe-success jurisdiction-id voter-id params]
                 :on-failure [::voter-sms-check-and-subscribe-failure]}}))

(rf/reg-event-fx
 ::voter-sms-check-and-subscribe-success
 (fn [{:keys [db]} [_ jurisdiction-id voter-id params response]]
   (cond-> {:db (assoc-in db [:widget :subscriptions :sms :stopcode-received] response)}
     (nil? (:stopcode-received/to-number response))
     (assoc :fx [[:dispatch [::voter-sms-subscribe jurisdiction-id voter-id params]]]))))

(rf/reg-event-db
 ::voter-sms-check-and-subscribe-failure
 (fn [db [_ _]]
   (assoc-in db [:widget :subscriptions :sms] nil)))

;; Email subscriptions

(rf/reg-event-fx
 ::voter-email-subscribe
 (fn [{:keys [db]} [_ jurisdiction-id voter-id params]]
   {:db (assoc-in db [:widget :subscriptions :email :subscribe] :standby)
    :http-xhrio {:method :post
                 :uri (urls/voter-email-subscribe jurisdiction-id voter-id)
                 :params params
                 :format (edn-request-format)
                 :response-format (edn-response-format)
                 :on-success [::voter-email-subscribe-success]
                 :on-failure [::voter-email-subscribe-failure]}}))

(rf/reg-event-db
 ::voter-email-subscribe-success
 (fn [db [_ response]]
   (assoc-in db
             [:widget :subscriptions :email]
             {:subscribe :sent
              :subscribe-response response})))

(rf/reg-event-db
 ::voter-email-subscribe-failure
 (fn [db [_ response]]
   (assoc-in db
             [:widget :subscriptions :email]
             {:subscribe-error response})))

(rf/reg-event-db
 ::voter-email-subscribe-reset
 (fn [db [_ _]]
   (assoc-in db [:widget :subscriptions :email] nil)))

(rf/reg-event-fx
 ::voter-email-unsubscribe
 (fn [_ [_ jurisdiction-id voter-id params]]
   {:http-xhrio {:method :put
                 :uri (urls/voter-email-unsubscribe jurisdiction-id voter-id)
                 :params params
                 :format (edn-request-format)
                 :response-format (edn-response-format)
                 :on-success [::voter-email-unsubscribe-success]
                 :on-failure [::voter-email-unsubscribe-failure]}}))

(rf/reg-event-db
 ::voter-email-unsubscribe-success
 (fn [db [_ response]]
   (assoc-in db
             [:widget :subscriptions :email]
             {:unsubscribe :sent
              :unsubscribe-response response})))

(rf/reg-event-db
 ::voter-email-unsubscribe-failure
 (fn [db [_ response]]
   (assoc-in db
             [:widget :subscriptions :email]
             {:unsubscribe-error response})))

;; Demo events

(defn capture-key
  "Given `ctrl` a `keycode`, execute function `f` "
  [keycode-map]
  (let [key-handler (KeyHandler. js/document)
        press-fn (fn [key-press]
                   ;; First ensure the user is holding ctrl
                   (when (.. key-press -ctrlKey)
                     ;; Then check the keycode. Check against `.keyCode` and `.which` (browser compatibility)
                     (when-let [f (get keycode-map (or (.. key-press -keyCode)
                                                       (.. key-press -which)))]
                       (f))))]
    (gev/listen key-handler
                (-> ^js KeyHandler .-EventType .-KEY)
                press-fn)))

(rf/reg-event-fx
 ::set-demo-keystrokes
 (fn [_ _]
   (capture-key {keycodes/ONE #(rf/dispatch [::demo-set-ballot-status "disposition/created"])
                 keycodes/TWO #(rf/dispatch [::demo-set-ballot-status "disposition/mailed"])
                 keycodes/THREE #(rf/dispatch [::demo-set-ballot-status "voter-bound/in-transit"])
                 keycodes/FOUR #(rf/dispatch [::demo-set-ballot-status "voter-bound/out-for-delivery"])
                 keycodes/FIVE #(rf/dispatch [::demo-set-ballot-status "voter-bound/delivered"])
                 keycodes/SIX #(rf/dispatch [::demo-set-ballot-status "office-bound/in-transit"])
                 keycodes/SEVEN #(rf/dispatch [::demo-set-ballot-status "office-bound/out-for-delivery"])
                 keycodes/EIGHT #(rf/dispatch [::demo-set-ballot-status "office-bound/delivered"])
                 keycodes/NINE #(rf/dispatch [::demo-set-ballot-status "disposition/accepted"])
                 keycodes/F #(rf/dispatch [::demo-set-ballot-status "disposition/flagged"])
                 keycodes/R #(rf/dispatch [::demo-set-ballot-status "office-bound/returned"])
                 keycodes/B #(rf/dispatch [::demo-set-multiple-ballots])
                 keycodes/V #(rf/dispatch [::demo-set-multiple-voters])})
   {}))

(def demo-ballot #:ballot{:status "disposition/created"
                          :lastevent-ts #inst "2020-12-31T17:00:00.000-00:00"
                          :voter-bound-mailpiece #:mailpiece{:lastscan-city "Townsville" :lastscan-state "NY" :lastscan-ts #inst "2020-12-31T17:00:00.000-00:00"}
                          :office-bound-mailpiece #:mailpiece{:lastscan-city "Townsville" :lastscan-state "NY" :lastscan-ts #inst "2020-12-31T17:00:00.000-00:00" :estimated-delivery-date "2021-01-02"}})

(rf/reg-event-fx
 ::demo-set-ballot-status
 (fn [{:keys [db]} [_ ballot-status]]
   (let [ballot-response [(merge demo-ballot #:ballot{:id 0 :status ballot-status})]]
     {:db (assoc-in db [:widget :ballot-response] ballot-response)
      :dispatch-n [[::set-form-phase :show-result]
                   [::set-widget-ballot-id (:ballot/id (first ballot-response))]]})))

(rf/reg-event-fx
 ::demo-set-multiple-ballots
 (fn [{:keys [db]} _]
   (let [ballot-response
         [(merge demo-ballot #:ballot{:id 0
                                      :election #:election{:name "2020 December General Election"}
                                      :status "disposition/mailed"})
          (merge demo-ballot #:ballot{:id 1
                                      :election #:election{:name "2021 January Special Election"}
                                      :status "disposition/accepted"})]]
     {:db (assoc-in db [:widget :ballot-response] ballot-response)
      :dispatch [::set-form-phase :multiple-elections]})))

(rf/reg-event-fx
 ::demo-set-multiple-voters
 (fn [{:keys [db]} _]
   (let [voter-response
         [#:voter{:id 0 :fullname "Alpha Betason Jr."}
          #:voter{:id 1 :fullname "Alpha Middle Betason"}]]
     {:db (assoc-in db [:widget :voter-response] voter-response)
      :dispatch [::set-form-phase :multiple-voters]})))
